import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardCopy, PackageSearch, RefreshCw } from 'lucide-react'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { copyMaterialSummary, fetchMaterialSummary, fetchOverview } from '../api/calculation-pricing.api'
import { stundenText } from '../lib/kalkulation-labels'
import type { CalcCostKind, CalcMaterialRow, CalcOverview, Calculation } from '../types/calculation.types'
import { PreiseAktualisierenDialog } from './PreiseAktualisierenDialog'
import { AngeboteUebernehmen } from './anfragen/AngeboteUebernehmen'
import { Schlussblatt } from './Schlussblatt'
import { PositionenKarte } from './PositionenKarte'

const FARBE: Record<CalcCostKind, string> = {
  LABOR: 'bg-sky-500',
  MATERIAL: 'bg-amber-500',
  SUBCONTRACT: 'bg-violet-500',
  EQUIPMENT: 'bg-emerald-500',
  OTHER: 'bg-slate-400',
}
const menge = (n: number, stellen = 2) => n.toLocaleString('de-DE', { maximumFractionDigits: stellen })

function Kachel({ titel, wert, unter }: { titel: string; wert: string; unter?: string }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{titel}</div>
      <div className="text-lg font-semibold tabular-nums">{wert}</div>
      {unter && <div className="text-xs text-muted-foreground">{unter}</div>}
    </div>
  )
}

/**
 * AP-37 4.10 — **Reiter Übersicht**: Kennzahlen, Positionen (N1-12),
 * Kostenarten (Balken und Tabelle), Stunden je Phase und Qualifikation,
 * Schlussblatt (nur lesend), Materialauszug (in die Ablage für eine
 * Preisanfrage), Preisstand mit „Preise aktualisieren", Hinweise.
 * Kostenblöcke nur mit `calculation.cost.view` — der Server lässt sie sonst weg.
 */
/** Detailblatt O 10.: höchster Anfragestand der zusammengefassten Zeilen. */
const ANFRAGE_TEXT: Record<string, string> = { SKIPPED: 'nicht anfragen', DRAFT: 'im Entwurf', REQUESTED: 'angefragt', ANSWERED: 'Angebot da', APPLIED: 'übernommen' }

export function KalkulationsUebersicht({
  kalkulation,
  onGeaendert,
  onSaetze,
  onOeffnen,
}: {
  kalkulation: Calculation
  onGeaendert: () => void
  onSaetze: () => void
  onOeffnen: (positionId: string) => void
}) {
  const { user } = useAuthStore()
  const kosten = erfuelltRecht('calculation.cost.view', user?.permissions)
  // Detailblatt O 9.1: das Cockpit sieht nur, wer anfragen darf.
  const cockpit = erfuelltRecht('calculation.request.manage', user?.permissions)
  const darf = kalkulation.writable && erfuelltRecht('calculation.calculation.manage', user?.permissions)
  const [o, setO] = useState<CalcOverview | null>(null)
  const [material, setMaterial] = useState<CalcMaterialRow[]>([])
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [preiseOffen, setPreiseOffen] = useState(false)
  const [angeboteOffen, setAngeboteOffen] = useState(false)

  const laden = useCallback(async () => {
    try {
      const [u, m] = await Promise.all([fetchOverview(kalkulation.id), fetchMaterialSummary(kalkulation.id)])
      setO(u)
      setMaterial(m)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Übersicht ließ sich nicht laden.'))
    }
  }, [kalkulation.id])
  // Neu laden, wenn sich die Kalkulation geändert hat (Stand am Kopf).
  useEffect(() => {
    void laden()
  }, [laden, kalkulation.updatedAt, kalkulation.priceAfterDiscount])

  if (!o) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null
  const f = o.figures
  const kopieren = async () => {
    try {
      const r = await copyMaterialSummary(kalkulation.id, auswahl.size ? [...auswahl] : undefined)
      setHinweis(`${r.copied} Zeile(n) in die Ablage gelegt — einfügen in einer Preisanfrage.`)
      setAuswahl(new Set())
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Kopieren hat nicht geklappt.'))
    }
  }
  const hinweise: React.ReactNode[] = []
  if (o.notes.ratesChanged.length) hinweise.push(`Vorgabesätze geändert: ${o.notes.ratesChanged.join(', ')} — in Sätze & Aufschläge übernehmen, wenn gewünscht.`)
  if (o.notes.positionsWithoutPrice) hinweise.push(`${o.notes.positionsWithoutPrice} Position(en) ohne Preis.`)
  if (o.notes.gapsOpen) hinweise.push(`${o.notes.gapsOpen} Bieterlücke(n) offen.`)
  if (o.notes.unitsUntranslated) hinweise.push(`${o.notes.unitsUntranslated} Einheit(en) aus dem LV ohne Übersetzung.`)
  if (o.notes.rateMissing) hinweise.push(`${o.notes.rateMissing} Position(en) mit Arbeitsschritt ohne Satz.`)
  // Detailblatt O 10.: Angebote aus Materialanfragen, die noch nicht übernommen sind. AP-39 Nachtrag 2 (6.2): übernommen
  // wird hier in der Kalkulation, im Fenster „Angebote übernehmen" — das Cockpit bleibt für den Einkauf.
  if (o.notes.offersNotApplied)
    hinweise.push(
      <>
        {o.notes.offersNotApplied} Angebot(e) aus Materialanfragen nicht übernommen —{' '}
        {kosten && darf ? (
          <button type="button" className="text-primary hover:underline" onClick={() => setAngeboteOffen(true)}>
            vergleichen und übernehmen
          </button>
        ) : cockpit ? (
          <Link to={`/calculations/material-requests?tab=preisspiegel&calculationId=${kalkulation.id}`} className="text-primary hover:underline">
            im Preisspiegel ansehen
          </Link>
        ) : (
          'im Preisspiegel der Materialanfragen'
        )}
      </>,
    )

  return (
    <div className="space-y-4">
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {hinweise.length > 0 && (
        <ul className="space-y-1 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-foreground">
          {hinweise.map((h, i) => (
            <li key={i}>{h}</li>
          ))}
        </ul>
      )}

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kachel titel="Angebotssumme" wert={geldText(f.priceAfterDiscount) || '0,00'} unter={f.discountAmount ? `netto ${geldText(f.priceTotal)}, Nachlass ${geldText(f.discountAmount)}` : 'netto'} />
        {kosten && <Kachel titel="Einzelkosten" wert={geldText(f.costTotal ?? 0)} />}
        {kosten && (
          <Kachel
            titel="Deckungsbeitrag"
            wert={geldText(f.contributionMargin ?? 0)}
            unter={f.contributionMarginPercent != null ? `${f.contributionMarginPercent.toLocaleString('de-DE', { maximumFractionDigits: 1 })} %` : undefined}
          />
        )}
        <Kachel titel="Stunden" wert={`${stundenText(f.hoursTotal)} h`} unter={kosten && f.averageWage != null ? `Mittellohn ${geldText(f.averageWage)}` : undefined} />
        <Kachel titel="Gewicht" wert={`${menge(f.weightTotal, 1)} kg`} />
        <Kachel titel="Positionen" wert={`${f.positionsPriced} / ${f.positionsPriced + f.positionsOpen}`} unter={`ausgepreist${f.gapsOpen ? ` · ${f.gapsOpen} Lücke(n)` : ''}`} />
      </div>

      {/* N1-12: links die Positionen über zwei Zeilen, rechts Kostenarten und Stunden. */}
      <div className="grid gap-4 xl:grid-cols-2">
        <div className={o.costKinds ? 'xl:row-span-2' : undefined}>
          <PositionenKarte kalkulationId={kalkulation.id} stand={kalkulation.updatedAt} onOeffnen={onOeffnen} />
        </div>
        {o.costKinds && (
          <section className="rounded-lg border border-border bg-card p-4">
            <h3 className="mb-2 text-sm font-semibold">Kostenarten</h3>
            <div className="mb-3 flex h-3 overflow-hidden rounded-full bg-muted">
              {o.costKinds.map((c) => (c.share > 0 ? <div key={c.kind} className={FARBE[c.kind]} style={{ width: `${c.share}%` }} title={`${c.label} ${menge(c.share, 1)} %`} /> : null))}
            </div>
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="py-1 text-left font-normal">Kostenart</th>
                  <th className="py-1 text-right font-normal">Einzelkosten</th>
                  <th className="py-1 text-right font-normal">Aufschlag</th>
                  <th className="py-1 text-right font-normal">Preis</th>
                  <th className="py-1 text-right font-normal">Anteil</th>
                </tr>
              </thead>
              <tbody>
                {o.costKinds.map((c) => (
                  <tr key={c.kind} className="border-t border-border/60">
                    <td className="py-1">
                      <span className={`mr-2 inline-block h-2.5 w-2.5 rounded-full ${FARBE[c.kind]}`} />
                      {c.label}
                    </td>
                    <td className="py-1 text-right tabular-nums">{geldText(c.cost)}</td>
                    <td className="py-1 text-right tabular-nums">{geldText(c.surcharge)}</td>
                    <td className="py-1 text-right tabular-nums">{geldText(c.price)}</td>
                    <td className="py-1 text-right tabular-nums">{menge(c.share, 1)} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        <section className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 text-sm font-semibold">Stunden</h3>
          {o.hoursByPhase.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Arbeitsschritte.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { titel: 'je Phase', zeilen: o.hoursByPhase.map((h) => ({ k: h.key, name: h.name, h: h.hours })) },
                { titel: 'je Qualifikation', zeilen: o.hoursByQualification.map((h) => ({ k: h.code, name: h.name, h: h.hours })) },
              ].map((b) => (
                <table key={b.titel} className="w-full text-sm">
                  <thead className="text-xs text-muted-foreground">
                    <tr>
                      <th className="py-1 text-left font-normal">{b.titel}</th>
                      <th className="py-1 text-right font-normal">h</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b.zeilen.map((z) => (
                      <tr key={z.k} className="border-t border-border/60">
                        <td className="py-1">{z.name}</td>
                        <td className="py-1 text-right tabular-nums">{stundenText(z.h)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
            </div>
          )}
        </section>
      </div>

      {o.closing && <Schlussblatt closing={o.closing} onAendern={onSaetze} />}

      <section className="rounded-lg border border-border bg-card p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Materialauszug</h3>
          <span className="flex flex-wrap items-center gap-2">
            {o.priceAge && (o.priceAge.lines > 0 || o.priceAge.changed > 0) && (
              <button type="button" className="inline-flex items-center gap-1 rounded-md border border-amber-500/50 px-2.5 py-1 text-sm text-amber-800 dark:text-amber-300" onClick={() => setPreiseOffen(true)}>
                <RefreshCw className="h-4 w-4" /> Preise aktualisieren ({o.priceAge.lines})
              </button>
            )}
            {material.length > 0 && cockpit && (
              <Link
                to={`/calculations/material-requests?tab=material&calculationId=${kalkulation.id}&markieren=1`}
                className="inline-flex items-center gap-1 rounded-md border border-primary px-2.5 py-1 text-sm text-primary"
                title="Alle Materialzeilen dieser Kalkulation im Cockpit „Materialanfragen“ — markiert, zum Anfragen"
              >
                <PackageSearch className="h-4 w-4" /> Im Cockpit anfragen
              </Link>
            )}
            {material.length > 0 && (
              <button type="button" className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-sm" onClick={() => void kopieren()}>
                <ClipboardCopy className="h-4 w-4" /> {auswahl.size ? `${auswahl.size} in die Ablage` : 'Alles in die Ablage'}
              </button>
            )}
          </span>
        </div>
        {hinweis && <p className="mb-2 text-sm text-muted-foreground">{hinweis}</p>}
        {material.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch kein Material in den Blättern.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="w-6 py-1" />
                  <th className="py-1 text-left font-normal">Bezeichnung</th>
                  <th className="py-1 text-right font-normal">Menge</th>
                  <th className="py-1 text-left font-normal">Einheit</th>
                  <th className="py-1 text-right font-normal">Gewicht kg</th>
                  {kosten && <th className="py-1 text-right font-normal">Kosten</th>}
                  <th className="py-1 text-left font-normal">Preisstand</th>
                  <th className="py-1 text-left font-normal">Lieferant</th>
                  <th className="py-1 text-left font-normal">Anfrage</th>
                  <th className="py-1 text-right font-normal">Pos.</th>
                </tr>
              </thead>
              <tbody>
                {material.map((z) => (
                  <tr key={z.key} className="border-t border-border/60">
                    <td className="py-1">
                      <input
                        type="checkbox"
                        checked={auswahl.has(z.key)}
                        onChange={() => setAuswahl((a) => (a.has(z.key) ? new Set([...a].filter((x) => x !== z.key)) : new Set([...a, z.key])))}
                        aria-label={z.name}
                      />
                    </td>
                    <td className="py-1">
                      {z.name}
                      {z.articleNumber && <span className="ml-1 text-xs text-muted-foreground">{z.articleNumber}</span>}
                    </td>
                    <td className="py-1 text-right tabular-nums" title={`Bedarf ohne Verschnitt ${menge(z.requiredQuantity, 3)}`}>
                      {menge(z.quantity, 3)}
                    </td>
                    <td className="py-1">{z.unitCode ?? ''}</td>
                    <td className="py-1 text-right tabular-nums">{z.weight ? menge(z.weight, 1) : ''}</td>
                    {kosten && <td className="py-1 text-right tabular-nums">{geldText(z.cost ?? 0)}</td>}
                    <td className="py-1 text-xs text-muted-foreground">{z.priceAgeDays != null ? `vor ${z.priceAgeDays} T.` : '—'}</td>
                    <td className="py-1 text-xs">{z.suppliers.join(', ')}</td>
                    <td className="py-1 text-xs">{z.requestState && z.requestState !== 'OPEN' ? ANFRAGE_TEXT[z.requestState] : ''}</td>
                    <td className="py-1 text-right tabular-nums">{z.positionCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-xs text-muted-foreground">Menge = zu beschaffen, samt Verschnitt; nur zählende Positionen (ohne Eventual- und Wahlpositionen).</p>
      </section>

      {preiseOffen && (
        <PreiseAktualisierenDialog
          kalkulationId={kalkulation.id}
          darf={darf}
          onClose={() => setPreiseOffen(false)}
          onDone={(n) => {
            setPreiseOffen(false)
            setHinweis(`${n} Preis(e) aktualisiert.`)
            void laden()
            onGeaendert()
          }}
        />
      )}
      {angeboteOffen && (
        <AngeboteUebernehmen
          kalkulation={kalkulation}
          onClose={() => setAngeboteOffen(false)}
          onGeaendert={() => {
            void laden()
            onGeaendert()
          }}
        />
      )}
    </div>
  )
}
